import { csrfHeaders } from "../auth/session-api.js";

/* Tenant configuration read + narrow profile update (PB-46, over the PB-45 API;
   PB-552/AC-04b widened the update from displayName-only to
   displayName/shortName/domain, so all three now persist server-side).
   Everything on this view except those three is owned elsewhere: the slug
   resolves the tenant at login, status is tenant lifecycle, identity-provider
   state is a non-authoritative projection written only by the deployment
   bootstrap (ADR-0021), and the sync counters are derived at read time. */

export type IdentityProviderSetupStatus =
  | "not_configured"
  | "configured"
  | "needs_attention";

export interface TenantIdentityProviderState {
  provider: "microsoft_entra" | null;
  status: IdentityProviderSetupStatus;
  configuredAt: string | null;
  lastCheckedAt: string | null;
}

export type TenantModuleStatus = "enabled" | "disabled";

/**
 * One row of the tenant's module matrix. `enabledAt` is nullable since PB-47 —
 * see the Auth contract; it is null both for a module with no enablement row and
 * for one whose only recorded decision was a disable.
 */
export interface TenantModuleState {
  moduleKey: string;
  displayName: string;
  status: TenantModuleStatus;
  enabledAt: string | null;
  disabledAt: string | null;
}

export interface TenantSyncState {
  lastSyncedAt: string | null;
  pendingCount: number;
  /* PB-5435. Changes held until a member's first sign-in, and the part of
     `pendingCount` the worker has stopped moving. Optional so a shell deployed
     ahead of Auth keeps rendering: an older Auth omits both (and still counts
     waiting changes in `pendingCount`), and reading them as 0 reproduces the
     card it drew. */
  waitingIdentityCount?: number;
  stalledCount?: number;
  deadLetterCount: number;
}

export interface TenantConfiguration {
  tenantId: string;
  slug: string;
  displayName: string;
  /** Both nullable: a tenant that has set neither is not an error state. */
  shortName: string | null;
  domain: string | null;
  status: "active" | "suspended" | "archived";
  identityProvider: TenantIdentityProviderState;
  modules: TenantModuleState[];
  sync: TenantSyncState;
  createdAt: string;
  updatedAt: string;
}

/* A full replace, mirroring the server: every field is stated explicitly, and
   `null` clears one — a missing key is a 400 rather than a silent wipe. */
export interface UpdateTenantProfileRequest {
  displayName: string;
  shortName: string | null;
  domain: string | null;
}

export const CONTROL_OR_SURROGATE = /[\p{Cc}\p{Cs}]/u;

export const DISPLAY_NAME_MAX = 255;

/* Mirrors parseProfileUpdate in the auth service, in the same order and with
   the same counting, so the form never blocks a name the server would take (or
   waves through one it would reject):
     - every rule is applied to the TRIMMED value, because the trim is what
       `updateTenantProfile` sends. Measuring the raw input instead rejects 250
       characters followed by six spaces, even though the 250-character payload
       that would actually go on the wire is one the server accepts;
     - the bound counts code points — String.length counts UTF-16 units, which
       would reject a 128-emoji name the contract and the column both accept;
     - isolated surrogates are rejected alongside control characters, since a
       lone half of a pair breaks the jsonb audit-payload write. */
export function validateDisplayName(value: string): string | null {
  const trimmed = value.trim();
  if (countCodePointsBounded(trimmed, DISPLAY_NAME_MAX) > DISPLAY_NAME_MAX) {
    return `Organization name must be ${DISPLAY_NAME_MAX} characters or fewer.`;
  }
  if (trimmed.length === 0) return "Organization name is required.";
  if (CONTROL_OR_SURROGATE.test(trimmed)) {
    return "Organization name must not contain control characters.";
  }
  return null;
}

export const SHORT_NAME_MAX = 6;

/* Short name / wordmark. Blank is allowed, where PB-46's mock-backed version
   required a value: PB-552 made the column nullable and the server normalizes an
   empty or whitespace-only value to `null`, so an unset wordmark is a state
   rather than a validation error. */
export function validateShortName(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (countCodePointsBounded(trimmed, SHORT_NAME_MAX) > SHORT_NAME_MAX) {
    return `Short name must be ${SHORT_NAME_MAX} characters or fewer.`;
  }
  if (CONTROL_OR_SURROGATE.test(trimmed)) {
    return "Short name must not contain control characters.";
  }
  return null;
}

export const DOMAIN_MAX = 253;

/* Hostname shape only — this deliberately does NOT prove the tenant owns the
   domain. Verification is out of scope for PB-552 too; it is a label, not a
   trust boundary. Blank is allowed: not every tenant federates a domain.

   The pattern is the server's tenants_domain_shape_check character for
   character, including its trailing `*`. A single-label host is something the
   column accepts, so rejecting it here would block a value the save would have
   stored — the same "never block what the server takes" rule the display-name
   validator follows. Lower-cased before the test, as the server does: hostnames
   are case-insensitive but the constraint is not. */
const DOMAIN_SHAPE =
  /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/;

export function validateDomain(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  if (trimmed.length === 0) return null;
  if (countCodePointsBounded(trimmed, DOMAIN_MAX) > DOMAIN_MAX) {
    return `Domain must be ${DOMAIN_MAX} characters or fewer.`;
  }
  if (!DOMAIN_SHAPE.test(trimmed)) {
    return "Enter a valid domain, like northwind.io";
  }
  return null;
}

/** Stops one past `limit`, so a pasted wall of text costs a fixed walk rather
    than materialising a code-point array for the whole value. */
export function countCodePointsBounded(value: string, limit: number): number {
  const codePoints = value[Symbol.iterator]();
  let count = 0;
  while (!codePoints.next().done) {
    count += 1;
    if (count > limit) return count;
  }
  return count;
}

/** Shared cache key for the tenant configuration — Organization and Identity &
    directory both read it, so they share one fetch and cannot disagree about
    scoping. Scoped by tenant for the same reason `settingsQueryKey` is: an admin
    session can be replaced in place (`/me` refetches on window focus) while the
    route stays mounted, and an unscoped key would then serve the previous
    tenant's configuration out of the cache. */
export function tenantConfigurationQueryKey(
  tenantId: string | null | undefined,
): readonly unknown[] {
  return ["auth", "admin", "tenant-configuration", tenantId ?? "none"] as const;
}

/** Carries the server's machine `code` alongside the message, so a caller with
    curated per-code copy (the module toggle's `MODULE_ERROR_COPY`) can key on it
    while callers without one still render `message`. */
export class TenantConfigApiError extends Error {
  readonly code: string | undefined;

  constructor(message: string, code: string | undefined) {
    super(message);
    this.name = "TenantConfigApiError";
    this.code = code;
  }
}

export async function getTenantConfiguration(): Promise<TenantConfiguration> {
  return (await request(
    "/api/auth/admin/tenant-configuration",
  )) as TenantConfiguration;
}

export async function updateTenantProfile(
  profile: UpdateTenantProfileRequest,
): Promise<TenantConfiguration> {
  return (await request("/api/auth/admin/tenant-configuration/profile", {
    method: "PUT",
    body: JSON.stringify(profile),
  })) as TenantConfiguration;
}

/**
 * Enable or disable one module for the caller's tenant (PB-47 / AC-05). Returns
 * the whole configuration back, so the caller can seed the cache from the
 * server's view rather than optimistically guessing what changed.
 *
 * `includeDependencies` (d2, ADR-0100) moves the modules this one needs, or
 * that need it, in the same write. Without it a change that would break a
 * requirement is refused `409 module_dependency_required`.
 */
export async function setTenantModuleStatus(
  moduleKey: string,
  status: TenantModuleStatus,
  options: { readonly includeDependencies?: boolean } = {},
): Promise<TenantConfiguration> {
  return (await request(
    `/api/auth/admin/tenant-configuration/modules/${encodeURIComponent(moduleKey)}`,
    {
      method: "PUT",
      body: JSON.stringify(
        options.includeDependencies
          ? { status, includeDependencies: true }
          : { status },
      ),
    },
  )) as TenantConfiguration;
}

/* Message-first, where admin-users-api is code-first — and since PB-540 that is a
   choice about copy rather than about shape. Auth's global exception filter now
   guarantees `{ code, message }` on every failure, so both keys are always
   available. `admin-users-api` prefers `code` because `ADMIN_ERROR_COPY` is keyed
   on codes and carries curated per-member sentences. This form has no such map:
   the server's own `message` — "Only displayName, shortName, and domain are
   accepted.", or the length and character rules — IS the sentence the admin needs
   to read, so preferring it avoids putting a machine code in front of them. `code`
   stays as the fallback for the two auth bodies that ship one without a message —
   and is also carried on the thrown error, for the module toggle, which does key
   copy on codes (PB-47). */
async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...csrfHeaders(),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    throw new TenantConfigApiError(
      body?.message ?? body?.code ?? `Request failed (${response.status})`,
      body?.code,
    );
  }
  /* The success path needs the same guard the failure path already has. Nothing
     enforces "200 implies JSON" at runtime — an ingress or proxy answering 200
     with an HTML page is enough — and OrganizationAdmin renders this message
     straight into its error Alert, so an unguarded parse puts the engine's
     `Unexpected token '<'` in front of an administrator. */
  try {
    return await response.json();
  } catch {
    throw new Error("The server returned a response we could not read.");
  }
}
